import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import type { StayPlanModule } from "@/lib/daycare-hotel/stayPlans";

const invalidateStayQueries = (queryClient: ReturnType<typeof useQueryClient>, clinicId: string | null) => {
  queryClient.invalidateQueries({ queryKey: ["panel-day", clinicId] });
  queryClient.invalidateQueries({ queryKey: ["stay-schedule", clinicId] });
  queryClient.invalidateQueries({ queryKey: ["stay-plan-contracts"] });
  queryClient.invalidateQueries({ queryKey: ["patient-history-feed"] });
  queryClient.invalidateQueries({ queryKey: ["owner-history-feed"] });
  queryClient.invalidateQueries({ queryKey: ["pet-reservation-summary"] });
};

/**
 * Cancela a reserva inteira: `stay_reservations.status`, todos os dias.
 * Solta todos os holds do plano antes — nada foi debitado, então não há
 * estorno, só liberação do que estava segurado.
 */
export async function cancelStayReservation({
  reservationId,
  module,
  cancellationDate,
}: {
  reservationId: string;
  module: StayPlanModule;
  cancellationDate: string;
}): Promise<void> {
  const { data: days, error: daysError } = await supabase
    .from("stay_reservation_days")
    .select("id, stay_date, status")
    .eq("stay_reservation_id", reservationId)
    .gte("stay_date", cancellationDate);
  if (daysError) throw daysError;

  for (const day of days ?? []) {
    for (const source of ["checkin", "hold"] as const) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: releaseError } = await (supabase as any).rpc("release_stay_plan_units", {
        p_module: module,
        p_reservation_id: reservationId,
        p_used_on: day.stay_date,
        p_source: source,
      });
      if (releaseError) throw releaseError;
    }
  }

  const dayIds = (days ?? [])
    .filter((day) => day.status === "scheduled" || day.status === "checked_in")
    .map((day) => day.id);
  if (dayIds.length > 0) {
    const { error: daysUpdateError } = await supabase
      .from("stay_reservation_days")
      .update({ status: "cancelled" })
      .in("id", dayIds);
    if (daysUpdateError) throw daysUpdateError;
  }

  const { error } = await supabase
    .from("stay_reservations")
    .update({ status: "cancelled" })
    .eq("id", reservationId);
  if (error) throw error;
}

export function useCancelStayReservation() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: cancelStayReservation,
    onSuccess: () => invalidateStayQueries(queryClient, clinicId),
  });
}

/**
 * Cancela só o acompanhamento do dia (`stay_reservation_days.status`),
 * mantendo o resto da reserva. Solta só o hold daquele dia.
 */
export async function cancelStayDay({
  stayDayId,
  reservationId,
  module,
  stayDate,
  stayDayStatus,
}: {
  stayDayId: string;
  reservationId: string;
  module: StayPlanModule;
  stayDate: string;
  stayDayStatus?: string;
}): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { error: releaseError } = await (supabase as any).rpc("release_stay_plan_units", {
    p_module: module,
    p_reservation_id: reservationId,
    p_used_on: stayDate,
    p_source: stayDayStatus === "checked_in" ? "checkin" : "hold",
  });
  if (releaseError) throw releaseError;

  const { error } = await supabase
    .from("stay_reservation_days")
    .update({ status: "cancelled" })
    .eq("id", stayDayId);
  if (error) throw error;
}

export function useCancelStayDay() {
  const queryClient = useQueryClient();
  const { data: profile } = useProfile();
  const clinicId = profile?.current_clinic_id ?? null;

  return useMutation({
    mutationFn: cancelStayDay,
    onSuccess: () => invalidateStayQueries(queryClient, clinicId),
  });
}
